import { Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Axios from '@/shared/api/axios';
import StatusModal from '@/components/ui/StatusModal';
import LoadingButton from '@/components/ui/LoadingButton';
import MessageModal from '@/components/ui/MessageModal';
import { useUnmountSafeTimeout } from '@/hooks/useUnmountSafeTimeout';


// 會員Forget the password表單
function ForgetForm() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    // 成功後延遲導頁用：期間若已離開頁面，卸載時清掉 timer，不會被拉回來
    const setRedirectTimeout = useUnmountSafeTimeout();

    // Status modal for errors
    const statusModalRef = useRef(null);
    const [currentStatusCode, setCurrentStatusCode] = useState('');

    // Success modal for password reset
    const successModalRef = useRef(null);

    const [validated, setValidated] = useState(false);
    const [resendCountdown, setResendCountdown] = useState(0);
    const [isResending, setIsResending] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [form, setForm] = useState({
        email: '',
        code: '',
        new_password: '',
        method: 'forget'
    })

    const handleChange = e => {
        setForm(({
            ...form,
            [e.target.name]: e.target.value,
        }))
    }

    // 倒計時功能
    useEffect(() => {
        let timer;
        if (resendCountdown > 0) {
            timer = setTimeout(() => {
                setResendCountdown(resendCountdown - 1);
            }, 1000);
        }
        return () => clearTimeout(timer);
    }, [resendCountdown]);

    // 發送驗證碼函數
    const handleSendVerification = async () => {
        if (resendCountdown > 0 || isResending || !form.email) return;

        setIsResending(true);

        try {
            const formData = await Axios.post("/user/verify-email", {
                email: form.email,
                method: "forget"
            });

            const statusCode = formData.data.status_code;

            if (statusCode === "SYSTEM_1000") {
                setResendCountdown(60);
            } else {
                // Show error modal for any non-success status
                setCurrentStatusCode(statusCode);
                statusModalRef.current?.show();
            }
        } catch (error) {
            console.error("Send verification code error:", error);
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            }
        } finally {
            setIsResending(false);
        }
    };

    const handleSubmit = async (event) => {
        const formCheck = event.currentTarget;
        event.preventDefault();

        if (formCheck.checkValidity() === false) {
            event.stopPropagation();
            setValidated(true);
            return;
        }

        setIsSubmitting(true);
        try {
            const formData = await Axios.post("/user/forget-password", form);
            const statusCode = formData.data.status_code;

            if (statusCode === 'SYSTEM_1000') {
                // Show success message with specific context
                successModalRef.current?.show();
                // 登入頁就是首頁（未登入時 "/" 顯示登入）；站內沒有 /login 路由
                setRedirectTimeout(() => navigate('/'), 2000);
            } else {
                // Show error modal using errorCodes mapping
                setCurrentStatusCode(statusCode);
                statusModalRef.current?.show();
            }
        } catch (error) {
            console.error("Forget password error:", error);
            // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
            if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
                setCurrentStatusCode(error.httpErrorCode);
                statusModalRef.current?.show();
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                {/* Email */}
                <Form.Group className="mb-3">
                    <Form.Control
                        onChange={handleChange}
                        name="email"
                        type="email"
                        placeholder={t('email')}
                        required
                        disabled={resendCountdown > 0}
                    />
                    <Form.Control.Feedback type="invalid">
                        {t("please_email")}
                    </Form.Control.Feedback>
                </Form.Group>

                {/* Send Verification Button with Verification Code */}
                <Form.Group className="mb-3">
                    <div className="d-flex gap-2">
                        <Button
                            disabled={resendCountdown > 0 || isResending || !form.email}
                            onClick={handleSendVerification}
                            className="btn btn-primaryF text-nowrap flex-shrink-0"
                            type="button"
                            style={{ width: '230px' }}
                        >
                            {resendCountdown > 0
                                ? t("resend_in_seconds", { seconds: resendCountdown })
                                : t("send_verification_letter")
                            }
                        </Button>
                        <Form.Control
                            onChange={handleChange}
                            name="code"
                            placeholder={t('verification_code')}
                            required
                            className="flex-grow-1"
                        />
                    </div>
                    <Form.Control.Feedback type="invalid">
                        {t("check_email_code")}
                    </Form.Control.Feedback>
                </Form.Group>

                {/* New Password */}
                <Form.Group className="mb-3">
                    <Form.Control
                        onChange={handleChange}
                        name="new_password"
                        type="password"
                        placeholder={t('new_password')}
                        pattern="[A-Za-z0-9_@#\-]{8,255}"
                        required
                    />
                    <Form.Control.Feedback type="invalid">
                        {t('password_requirement')}
                    </Form.Control.Feedback>
                </Form.Group>

                {/* Submit Button */}
                <div>
                    <LoadingButton
                        className="btn btn-primary btn-lg w-100 mb-3 btn-primaryF"
                        type="submit"
                        isLoading={isSubmitting}
                    >
                        {t("confirm")}
                    </LoadingButton>
                </div>
            </Form>

            {/* Error Status Modal */}
            <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />

            {/* Success Modal for Password Reset */}
            <MessageModal ref={successModalRef} status="success">
                <p>{t("password_reset_success")}</p>
            </MessageModal>
        </>
    );
}

export default ForgetForm;